<template>
    <main
        class="min-w-0 flex-1 border-t border-taupe-200 lg:flex overflow-y-auto overflow-x-hidden"
    >
        <!-- Primary column -->
        <section
            aria-labelledby="primary-heading"
            class="flex h-full min-w-0 flex-1 flex-col lg:order-last pt-2 sm:pt-4 lg:pt-6 lg:max-w-7xl lg:mx-auto"
        >
            <div class="px-4 sm:px-6 lg:px-8">
                <div class="sm:flex sm:items-center">
                    <div class="sm:flex sm:space-x-4">
                        <h1
                            id="primary-heading"
                            class="text-2xl font-medium text-taupe-800"
                        >
                            Reports
                        </h1>
                    </div>
                </div>

                <div class="mt-2 sm:mt-4 space-y-2">
                    <div class="sm:flex sm:items-center">
                        <div class="sm:flex">
                            <h2 class="text-xl font-medium text-taupe-800">
                                Projects
                            </h2>
                        </div>
                    </div>
                    <div class="w-full grid gap-4 grid-cols-2 lg:grid-cols-3">
                        <report-card
                            :to="{
                                name: 'ProjectReportsFlagStatusOverviewScreen',
                            }"
                            title="Flag Status Overview"
                        >
                            <template #icon>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    aria-hidden="true"
                                    viewBox="0 0 24 24"
                                    stroke-width="1"
                                    stroke="currentColor"
                                    fill="none"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                >
                                    <path
                                        stroke="none"
                                        d="M0 0h24v24H0z"
                                        fill="none"
                                    />
                                    <path d="M9 12l.01 0" />
                                    <path
                                        d="M9 6l11 0a2 2 0 0 1 2 2l0 8a2 2 0 0 1 -2 2l-11 0a2 2 0 0 1 -2 -2l0 -8a2 2 0 0 1 2 -2z"
                                    />
                                    <path d="M13 10l4 0" />
                                    <path d="M13 14l4 0" />
                                </svg>
                            </template>
                            A live snapshot of every flag's current state across
                            your project.
                        </report-card>

                        <report-card
                            v-if="hasPlanFeature('flag_lifecycle_management')"
                            :to="{
                                name: 'ProjectReportsStaleFlagsScreen',
                            }"
                            title="Stale Flags"
                        >
                            <template #icon>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    aria-hidden="true"
                                    viewBox="0 0 24 24"
                                    stroke-width="1"
                                    stroke="currentColor"
                                    fill="none"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                >
                                    <path
                                        stroke="none"
                                        d="M0 0h24v24H0z"
                                        fill="none"
                                    />
                                    <path
                                        d="M8 5h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h5.697"
                                    />
                                    <path d="M18 14v4h4" />
                                    <path d="M18 11v-4a2 2 0 0 0 -2 -2h-2" />
                                    <path
                                        d="M8 3m0 2a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v0a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2z"
                                    />
                                    <path
                                        d="M18 18m-4 0a4 4 0 1 0 8 0a4 4 0 1 0 -8 0"
                                    />
                                    <path d="M8 11h4" />
                                    <path d="M8 15h3" />
                                </svg>
                            </template>
                            Flags that haven't been evaluated recently, ready to
                            review and archive.
                        </report-card>

                        <report-card
                            :to="{
                                name: 'ProjectReportsMissingDefaultValueScreen',
                            }"
                            title="Feature Flag Missing Default Value"
                        >
                            <template #icon>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    aria-hidden="true"
                                    viewBox="0 0 24 24"
                                    stroke-width="1"
                                    stroke="currentColor"
                                    fill="none"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                >
                                    <path
                                        stroke="none"
                                        d="M0 0h24v24H0z"
                                        fill="none"
                                    />
                                    <path
                                        d="M8 5h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h5.697"
                                    />
                                    <path d="M18 14v4h4" />
                                    <path d="M18 11v-4a2 2 0 0 0 -2 -2h-2" />
                                    <path
                                        d="M8 3m0 2a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v0a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2z"
                                    />
                                    <path
                                        d="M18 18m-4 0a4 4 0 1 0 8 0a4 4 0 1 0 -8 0"
                                    />
                                    <path d="M8 11h4" />
                                    <path d="M8 15h3" />
                                </svg>
                            </template>
                            Flags missing a default value in case Zenmanage is
                            unreachable.
                        </report-card>

                        <report-card
                            :to="{
                                name: 'ProjectReportsEvaluationVolumeScreen',
                            }"
                            title="Flag Evaluation Volume"
                        >
                            <template #icon>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    aria-hidden="true"
                                    viewBox="0 0 24 24"
                                    stroke-width="1"
                                    stroke="currentColor"
                                    fill="none"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                >
                                    <path
                                        stroke="none"
                                        d="M0 0h24v24H0z"
                                        fill="none"
                                    />
                                    <path d="M4 19l16 0" />
                                    <path d="M4 15l4 -6l4 2l4 -5l4 4" />
                                </svg>
                            </template>
                            Evaluation counts per flag over time, broken down by
                            environment.
                        </report-card>
                    </div>
                </div>

                <div
                    v-if="hasRolloutHealthReports"
                    class="mt-6 sm:mt-8 space-y-2"
                >
                    <div class="sm:flex sm:items-center">
                        <div class="sm:flex">
                            <h2 class="text-xl font-medium text-taupe-800">
                                Rollout Health
                            </h2>
                        </div>
                    </div>
                    <div class="w-full grid gap-4 grid-cols-2 lg:grid-cols-3">
                        <report-card
                            v-if="hasPlanFeature('advanced_reporting')"
                            :to="{
                                name: 'ProjectReportsRolloutProgressScreen',
                            }"
                            title="Rollout Progress"
                        >
                            <template #icon>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    aria-hidden="true"
                                    viewBox="0 0 24 24"
                                    stroke-width="1"
                                    stroke="currentColor"
                                    fill="none"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                >
                                    <path
                                        stroke="none"
                                        d="M0 0h24v24H0z"
                                        fill="none"
                                    />
                                    <path
                                        d="M17 17m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0"
                                    />
                                    <path
                                        d="M9 7m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0"
                                    />
                                    <path d="M17 7l-10 10" />
                                </svg>
                            </template>
                            Exposure percentage and stage-by-stage timeline for
                            active progressive rollouts. Requires audit log
                            access.
                        </report-card>
                    </div>
                </div>

                <div
                    v-if="hasUserExposureReports"
                    class="mt-6 sm:mt-8 space-y-2"
                >
                    <div class="sm:flex sm:items-center">
                        <div class="sm:flex">
                            <h2 class="text-xl font-medium text-taupe-800">
                                User Exposure
                            </h2>
                        </div>
                    </div>
                    <div class="w-full grid gap-4 grid-cols-2 lg:grid-cols-3">
                        <report-card
                            v-if="hasPlanFeature('advanced_reporting')"
                            :to="{
                                name: 'ProjectReportsUserExposureBySegmentScreen',
                            }"
                            title="User Exposure by Segment"
                        >
                            <template #icon>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    aria-hidden="true"
                                    viewBox="0 0 24 24"
                                    stroke-width="1"
                                    stroke="currentColor"
                                    fill="none"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                >
                                    <path
                                        stroke="none"
                                        d="M0 0h24v24H0z"
                                        fill="none"
                                    />
                                    <path
                                        d="M9 7m-4 0a4 4 0 1 0 8 0a4 4 0 1 0 -8 0"
                                    />
                                    <path
                                        d="M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2"
                                    />
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                    <path d="M21 21v-2a4 4 0 0 0 -3 -3.85" />
                                </svg>
                            </template>
                            Exposure by user segment, with unexpected or
                            zero-exposure segments flagged.
                        </report-card>

                        <report-card
                            v-if="hasPlanFeature('advanced_reporting')"
                            :to="{
                                name: 'ProjectReportsGeographicExposureScreen',
                            }"
                            title="Geographic Exposure"
                        >
                            <template #icon>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    aria-hidden="true"
                                    viewBox="0 0 24 24"
                                    stroke-width="1"
                                    stroke="currentColor"
                                    fill="none"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                >
                                    <path
                                        stroke="none"
                                        d="M0 0h24v24H0z"
                                        fill="none"
                                    />
                                    <path
                                        d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0"
                                    />
                                    <path d="M3.6 9h16.8" />
                                    <path d="M3.6 15h16.8" />
                                    <path d="M11.5 3a17 17 0 0 0 0 18" />
                                    <path d="M12.5 3a17 17 0 0 1 0 18" />
                                </svg>
                            </template>
                            World map of flag exposure by country, filterable by
                            flag and date range.
                        </report-card>
                    </div>
                </div>

                <div
                    v-if="hasAuditActivityReports"
                    class="mt-6 sm:mt-8 space-y-2"
                >
                    <div class="sm:flex sm:items-center">
                        <div class="sm:flex">
                            <h2 class="text-xl font-medium text-taupe-800">
                                Audit &amp; Activity
                            </h2>
                        </div>
                    </div>
                    <div class="w-full grid gap-4 grid-cols-2 lg:grid-cols-3">
                        <report-card
                            v-if="hasPlanFeature('audit_log_access')"
                            :to="{ name: 'ProjectReportsAuditLogScreen' }"
                            title="Audit Log"
                        >
                            <template #icon>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    aria-hidden="true"
                                    viewBox="0 0 24 24"
                                    stroke-width="1"
                                    stroke="currentColor"
                                    fill="none"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                >
                                    <path
                                        stroke="none"
                                        d="M0 0h24v24H0z"
                                        fill="none"
                                    />
                                    <path
                                        d="M9 5h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2h-2"
                                    />
                                    <path
                                        d="M9 3m0 2a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v0a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2z"
                                    />
                                    <path d="M9 12l.01 0" />
                                    <path d="M13 12l2 0" />
                                    <path d="M9 16l.01 0" />
                                    <path d="M13 16l2 0" />
                                </svg>
                            </template>
                            Searchable log of every flag change, exportable to
                            CSV. Requires audit log access.
                        </report-card>

                        <report-card
                            v-if="hasPlanFeature('audit_log_access')"
                            :to="{ name: 'ProjectReportsUserActivityScreen' }"
                            title="User Activity"
                        >
                            <template #icon>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    aria-hidden="true"
                                    viewBox="0 0 24 24"
                                    stroke-width="1"
                                    stroke="currentColor"
                                    fill="none"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                >
                                    <path
                                        stroke="none"
                                        d="M0 0h24v24H0z"
                                        fill="none"
                                    />
                                    <path
                                        d="M9 7m-4 0a4 4 0 1 0 8 0a4 4 0 1 0 -8 0"
                                    />
                                    <path
                                        d="M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2"
                                    />
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                    <path d="M21 21v-2a4 4 0 0 0 -3 -3.85" />
                                    <path d="M12 12l2.5 2.5" />
                                </svg>
                            </template>
                            Per-user activity summaries for access reviews.
                            Requires audit log access.
                        </report-card>

                        <report-card
                            v-if="hasPlanFeature('flag_lifecycle_management')"
                            :to="{
                                name: 'ProjectReportsFlagOwnershipCoverageScreen',
                            }"
                            title="Flag Ownership & Coverage"
                        >
                            <template #icon>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    aria-hidden="true"
                                    viewBox="0 0 24 24"
                                    stroke-width="1"
                                    stroke="currentColor"
                                    fill="none"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                >
                                    <path
                                        stroke="none"
                                        d="M0 0h24v24H0z"
                                        fill="none"
                                    />
                                    <path
                                        d="M9 7m-4 0a4 4 0 1 0 8 0a4 4 0 1 0 -8 0"
                                    />
                                    <path
                                        d="M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2"
                                    />
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                    <path d="M21 21v-2a4 4 0 0 0 -3 -3.85" />
                                </svg>
                            </template>
                            Flag ownership and documentation coverage, with
                            unowned flags flagged.
                        </report-card>

                        <report-card
                            v-if="hasPlanFeature('advanced_reporting')"
                            :to="{
                                name: 'ProjectReportsFlagChangesTimelineScreen',
                            }"
                            title="Flag Changes Over Time"
                        >
                            <template #icon>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    aria-hidden="true"
                                    viewBox="0 0 24 24"
                                    stroke-width="1"
                                    stroke="currentColor"
                                    fill="none"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                >
                                    <path
                                        stroke="none"
                                        d="M0 0h24v24H0z"
                                        fill="none"
                                    />
                                    <path d="M4 19l16 0" />
                                    <path d="M4 15l4 -6l4 2l4 -5l4 4" />
                                    <path d="M8 9l0 6" />
                                    <path d="M16 13l0 2" />
                                </svg>
                            </template>
                            Timeline of flag config changes overlaid against
                            evaluation volume. Requires audit log access.
                        </report-card>
                    </div>
                </div>

                <zen-denied-state
                    v-if="restrictedReportsMessage"
                    class="mt-6 sm:mt-8"
                    title="More reports on a higher plan"
                    :message="restrictedReportsMessage"
                    :action-to="canManageBilling ? billingRoute : null"
                    action-label="Upgrade plan"
                />
            </div>
        </section>
    </main>
</template>

<script>
import ReportCard from '@/screens/_components/ReportCard.vue'
import { computed } from 'vue'
import { usePlan } from '@/composables/usePlan'
import { usePermissions, PERMISSIONS } from '@/composables/usePermissions'

export default {
    name: 'ProjectReportsScreen',
    components: { ReportCard },
    setup() {
        const { hasPlanFeature } = usePlan()
        const { can } = usePermissions()

        const canManageBilling = computed(() => can(PERMISSIONS.BILLING_UPDATE))
        const billingRoute = { name: 'AdminBillingScreen' }

        const hasRolloutHealthReports = computed(() =>
            hasPlanFeature('advanced_reporting')
        )
        const hasUserExposureReports = computed(() =>
            hasPlanFeature('advanced_reporting')
        )
        const hasAuditActivityReports = computed(
            () =>
                hasPlanFeature('audit_log_access') ||
                hasPlanFeature('flag_lifecycle_management') ||
                hasPlanFeature('advanced_reporting')
        )

        // One consolidated message covers every gated section, rather than
        // repeating the same upgrade box once per hidden section.
        const restrictedReportsMessage = computed(() => {
            const sections = []

            if (!hasRolloutHealthReports.value) {
                sections.push('rollout health')
            }

            if (!hasUserExposureReports.value) {
                sections.push('user exposure')
            }

            if (!hasAuditActivityReports.value) {
                sections.push('audit & activity')
            }

            if (sections.length === 0) {
                return null
            }

            const list = new Intl.ListFormat('en', {
                style: 'long',
                type: 'conjunction',
            }).format(sections)

            return (
                `${list.charAt(0).toUpperCase()}${list.slice(1)} reports need ` +
                'a higher plan \u2014 no sales call, just upgrade in Billing.'
            )
        })

        return {
            hasPlanFeature,
            canManageBilling,
            billingRoute,
            hasRolloutHealthReports,
            hasUserExposureReports,
            hasAuditActivityReports,
            restrictedReportsMessage,
        }
    },
}
</script>
